import React, { useCallback, useEffect, useState } from "react";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { Box, Paper, Typography } from "@mui/material";
import moment from "moment";
import { toast } from "react-toastify";
import BaseTable, { buildQuery } from "../BaseTable/BaseTable";
import ScreenLoader from "../ScreenLoader";
import {
  exportCouponUsageReportCsv,
  getCouponUsageFilterOptions,
  getCouponUsageReport,
} from "../../redux/action/user";
import "./CouponUsageReport.scss";

const buildExportQuery = (filters) => {
  const activeFilters = filters.filter((filter) => {
    if (!filter.value) return false;
    if (filter.type === "date") {
      return filter.value.startDate && filter.value.endDate;
    }
    if (Array.isArray(filter.value)) {
      return filter.value.length > 0;
    }
    return filter.value !== "";
  });

  const filterQueryParts = [];
  activeFilters.forEach((filter) => {
    if (filter.type === "date") {
      const { startDate, endDate } = filter.value;
      filterQueryParts.push(`startDate=${moment(startDate).format("YYYY-MM-DD")}`);
      filterQueryParts.push(`endDate=${moment(endDate).format("YYYY-MM-DD")}`);
    } else if (Array.isArray(filter.value)) {
      filterQueryParts.push(`${filter.name}=${filter.value.map((v) => encodeURIComponent(v)).join(",")}`);
    } else {
      filterQueryParts.push(`${filter.name}=${encodeURIComponent(filter.value)}`);
    }
  });

  return filterQueryParts.length > 0 ? `?${filterQueryParts.join("&")}` : "";
};

const formatCurrency = (amount, currency = "usd") => {
  if (amount == null || amount === "") return "—";
  return `${Number(amount).toFixed(2)} ${String(currency).toUpperCase()}`;
};

const createInitialFilters = (options = { programs: [], affiliates: [] }) => [
  { id: 1, label: "Coupon Code", value: "", type: "input", name: "couponCode" },
  { id: 2, name: "date", label: "Date Range", value: "", type: "date" },
  {
    id: 3,
    label: "Purchase Source",
    name: "purchaseSource",
    value: "",
    type: "multi-select",
    dropArr: [
      { value: "vitality_portal", label: "Vitality Portal" },
      { value: "affiliate", label: "Affiliate" },
      { value: "landing_page", label: "Landing Page" },
    ],
  },
  {
    id: 10,
    label: "Purchase Type",
    name: "orderType",
    value: "",
    type: "multi-select",
    dropArr: [
      { value: "new_purchase", label: "New Purchase" },
      { value: "renewal", label: "Renewal" },
    ],
  },
  {
    id: 4,
    label: "Affiliate",
    name: "affiliate",
    value: "",
    type: "single-select",
    dropArr: options.affiliates,
  },
  {
    id: 5,
    label: "Program",
    name: "programId",
    value: "",
    type: "single-select",
    dropArr: options.programs,
  },
  {
    id: 6,
    label: "Membership",
    name: "membership",
    value: "",
    type: "multi-select",
    dropArr: [
      { value: "subscription", label: "Subscription" },
      { value: "program", label: "Program" },
    ],
  },
  {
    id: 7,
    label: "Payment Status",
    name: "paymentStatus",
    value: "",
    type: "multi-select",
    dropArr: [
      { value: "SUCCEEDED", label: "Succeeded" },
      { value: "PENDING", label: "Pending" },
      { value: "FAILED", label: "Failed" },
    ],
  },
  { id: 8, label: "Customer Name", value: "", type: "input", name: "customerName" },
  { id: 9, label: "Customer Email", value: "", type: "input", name: "customerEmail" },
];
const sanitizeTopCoupons = (coupons = []) =>
  coupons.filter(
    (item) => item.couponCode && String(item.couponCode).toLowerCase() !== "null"
  );

const CouponUsageReportTable = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [allTopCoupons, setAllTopCoupons] = useState([]);
  const [selectedCouponCodes, setSelectedCouponCodes] = useState([]);
  const [filters, setFilters] = useState(() => createInitialFilters());
  const [paginationParams, setPaginationParams] = useState({
    currentPage: 1,
    limit: 10,
    total: 1,
    totalRecords: 0,
  });
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const res = await getCouponUsageFilterOptions();
        const options = res.data?.data || { programs: [], affiliates: [] };
        setFilters((prev) =>
          prev.map((filter) => {
            if (filter.name === "programId") {
              return { ...filter, dropArr: options.programs };
            }
            if (filter.name === "affiliate") {
              return { ...filter, dropArr: options.affiliates };
            }
            return filter;
          })
        );
      } catch (error) {
        toast.error("Failed to load filter options.");
      }
    };

    loadFilterOptions();
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const query = buildQuery(
        filters,
        paginationParams.currentPage,
        paginationParams.limit
      );
      const res = await getCouponUsageReport(query);
      const data = res.data?.data || {};
      const couponFilter = filters.find((f) => f.name === "couponCode")?.value;

      setRows(data.rows || []);
      setSummary(data.summary || null);

      if (!couponFilter || !String(couponFilter).trim()) {
        setAllTopCoupons(sanitizeTopCoupons(data.summary?.topCoupons));
      }
      setPaginationParams((prev) => ({
        ...prev,
        currentPage: data.pagination?.currentPage || 1,
        limit: data.pagination?.pageSize || prev.limit,
        total: data.pagination?.totalPages || 1,
        totalRecords: data.pagination?.totalRecords || 0,
      }));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load coupon usage report.");
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [filters, paginationParams.currentPage, paginationParams.limit]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const syncCouponFilter = (codes) => {
    setFilters((prev) =>
      prev.map((filter) =>
        filter.name === "couponCode"
          ? { ...filter, value: codes.length ? codes.join(",") : "" }
          : filter
      )
    );
  };

  const handleFilterClick = (updatedFilters) => {
    setPaginationParams((prev) => ({
      ...prev,
      currentPage: 1,
    }));
    const couponValue = updatedFilters.find((f) => f.name === "couponCode")?.value || "";
    setSelectedCouponCodes(
      couponValue
        ? String(couponValue)
            .split(",")
            .map((code) => code.trim())
            .filter(Boolean)
        : []
    );
    setFilters(updatedFilters);
  };

  const handleTopCouponClick = (couponCode) => {
    if (!couponCode || String(couponCode).toLowerCase() === "null") return;

    setPaginationParams((prev) => ({
      ...prev,
      currentPage: 1,
    }));

    const next = selectedCouponCodes.includes(couponCode)
      ? selectedCouponCodes.filter((code) => code !== couponCode)
      : [...selectedCouponCodes, couponCode];

    setSelectedCouponCodes(next);
    syncCouponFilter(next);
  };

  const handleClearCouponSelection = () => {
    setPaginationParams((prev) => ({
      ...prev,
      currentPage: 1,
    }));
    setSelectedCouponCodes([]);
    syncCouponFilter([]);
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const res = await exportCouponUsageReportCsv(buildExportQuery(filters));
      const blob = new Blob([res.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "coupon_usage_report.csv";
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      let errorMessage = "Something went wrong during export.";
      if (
        error.response &&
        error.response.data instanceof Blob &&
        error.response.data.type === "application/json"
      ) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errorMessage = json?.message || errorMessage;
        } catch (parseError) {
          console.error("Failed to parse blob error:", parseError);
        }
      }
      toast.error(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  const headers = [
    { label: "Purchase Date", align: "center" },
    { label: "Customer Name", align: "center" },
    { label: "Customer Email", align: "center" },
    { label: "Program Name", align: "center" },
    { label: "Subscription/Membership", align: "center" },
    { label: "Purchase Source", align: "center" },
    { label: "Purchase Type", align: "center" },
    { label: "Affiliate Name", align: "center" },
    { label: "Coupon Code", align: "center" },
    { label: "Discount Applied", align: "center" },
    { label: "Amount Paid", align: "center" },
    { label: "Payment Status", align: "center" },
    { label: "Stripe Payment ID", align: "center" },
    { label: "Subscription ID", align: "center" },
    { label: "Order ID", align: "center" },
  ];

  const renderRows = () =>
    rows.map((row, index) => (
      <TableRow key={`${row.orderId || "order"}-${index}`}>
        <TableCell align="center">
          {row.purchaseDate ? moment(row.purchaseDate).format("MMM D, YYYY h:mm A") : "—"}
        </TableCell>
        <TableCell align="center">{row.customerName || "—"}</TableCell>
        <TableCell align="center">{row.customerEmail || "—"}</TableCell>
        <TableCell align="center">{row.programName || "—"}</TableCell>
        <TableCell align="center">{row.membershipType || "—"}</TableCell>
        <TableCell align="center">{row.purchaseSource || "—"}</TableCell>
        <TableCell align="center">{row.purchaseType || "—"}</TableCell>
        <TableCell align="center">{row.affiliateName || "—"}</TableCell>
        <TableCell align="center">{row.couponCode || "—"}</TableCell>
        <TableCell align="center">
          {row.discountApplied != null ? formatCurrency(row.discountApplied, row.currency) : "—"}
        </TableCell>
        <TableCell align="center">{formatCurrency(row.amountPaid, row.currency)}</TableCell>
        <TableCell align="center">{row.paymentStatus || "—"}</TableCell>
        <TableCell align="center">{row.stripePaymentId || "—"}</TableCell>
        <TableCell align="center">{row.subscriptionId || "—"}</TableCell>
        <TableCell align="center">{row.orderId || "—"}</TableCell>
      </TableRow>
    ));

  return (
    <div className="coupon-usage-report">
      {(loading || exporting) && <ScreenLoader />}

      {summary && (
        <Box className="coupon-usage-summary" mb={2}>
          <Box className="summary-metrics-row">
            <Paper className="summary-card summary-card-vitality" elevation={0}>
              <Typography className="summary-card-label">Vitality Portal</Typography>
              <Typography className="summary-card-value">
                {summary.byPurchaseSource?.vitality_portal ?? 0}
              </Typography>
            </Paper>
            <Paper className="summary-card summary-card-landing" elevation={0}>
              <Typography className="summary-card-label">Landing Page</Typography>
              <Typography className="summary-card-value">
                {summary.byPurchaseSource?.landing_page ?? 0}
              </Typography>
            </Paper>
          </Box>
          {allTopCoupons.length > 0 && (
            <Paper className="summary-card summary-card-wide" elevation={0}>
              <Box className="top-coupons-header">
                <Typography className="top-coupons-title">Top Coupons</Typography>
                <Typography className="top-coupons-hint">
                  Click to select one or more coupons
                </Typography>
                {selectedCouponCodes.length > 0 && (
                  <button
                    type="button"
                    className="top-coupons-clear"
                    onClick={handleClearCouponSelection}
                  >
                    Clear selection ({selectedCouponCodes.length})
                  </button>
                )}
              </Box>
              <Box className="top-coupons-list">
                {allTopCoupons.map((item) => (
                  <button
                    key={item.couponCode}
                    type="button"
                    className={`top-coupon-chip${
                      selectedCouponCodes.includes(item.couponCode) ? " active" : ""
                    }`}
                    onClick={() => handleTopCouponClick(item.couponCode)}
                    title={
                      selectedCouponCodes.includes(item.couponCode)
                        ? `Remove ${item.couponCode} from filter`
                        : `Add ${item.couponCode} to filter`
                    }
                  >
                    <span className="top-coupon-code">
                      {String(item.couponCode).toUpperCase()}
                    </span>
                    <span className="top-coupon-count">{item.count}</span>
                  </button>
                ))}
              </Box>
            </Paper>
          )}
        </Box>
      )}

      <BaseTable
        headers={headers}
        rows={rows}
        renderRows={renderRows}
        isLoading={loading}
        showPagination
        paginationParams={paginationParams}
        setPaginationParams={setPaginationParams}
        currentPage={paginationParams.currentPage}
        limit={paginationParams.limit}
        isFilter
        filters={filters}
        handleFilterClick={handleFilterClick}
        handleExportData={handleExportData}
        height="520px"
      />
    </div>
  );
};

export default CouponUsageReportTable;
